import React, { useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { createCheckout } from '../../api/checkouts';
import api from '../../api/client';
import { getBooks } from '../../api/books';
import { getMembers } from '../../api/members';
import Modal from '../ui/Modal';
import FormField, { Select, SubmitBtn } from '../ui/FormField';
import toast from 'react-hot-toast';
import { Search } from 'lucide-react';

export default function NewCheckoutModal({ open, onClose, preselectedBookId }: { open: boolean; onClose: () => void; preselectedBookId?: number }) {
  const qc = useQueryClient();
  const [bookQ, setBookQ] = useState('');
  const [memberQ, setMemberQ] = useState('');
  const [selectedBook, setSelectedBook] = useState<any>(null);
  const [preloaded, setPreloaded] = useState(false);
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const [selectedCopy, setSelectedCopy] = useState('');
  const [days, setDays] = useState(14);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'book'|'member'|'confirm'>('book');

  const { data: booksData } = useQuery({
    queryKey: ['books-search', bookQ],
    queryFn: () => getBooks({ q: bookQ, limit: 8 }),
    enabled: bookQ.length > 1,
  } as any);

  const { data: membersData } = useQuery({
    queryKey: ['members-search', memberQ],
    queryFn: () => getMembers({ q: memberQ, limit: 8 }),
    enabled: memberQ.length > 1,
  } as any);

  const books = (booksData as any)?.data?.books || [];

  const { data: preloadedBook } = useQuery({
    queryKey: ['book-preload', preselectedBookId],
    queryFn: () => import('../../api/books').then(m => m.getBook(preselectedBookId!)),
    enabled: !!preselectedBookId && open && !preloaded,
  } as any);

  React.useEffect(() => {
    if (preloadedBook && !preloaded && preselectedBookId) {
      const b = (preloadedBook as any)?.data;
      if (b) { setSelectedBook(b); setStep('member'); setPreloaded(true); }
    }
  }, [preloadedBook, preloaded, preselectedBookId]);

  React.useEffect(() => {
    if (!open) { setPreloaded(false); if (!preselectedBookId) { setSelectedBook(null); setStep('book'); } }
  }, [open, preselectedBookId]);

  const { data: copiesData } = useQuery({
    queryKey: ['available-copies', selectedBook?.id],
    queryFn: () => api.get(`/checkouts/available-copies/${selectedBook?.id}`),
    enabled: !!selectedBook?.id,
  } as any);

  const availableCopies = (copiesData as any)?.data?.copies || [];

  React.useEffect(() => {
    if (availableCopies.length === 1 && !selectedCopy) {
      setSelectedCopy(String(availableCopies[0].id));
    }
  }, [availableCopies]);
  const members = (membersData as any)?.data?.members || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBook || !selectedMember || !selectedCopy) return toast.error('ყველა ველი სავალდებულოა');
    setLoading(true);
    try {
      await createCheckout({ copy_id: parseInt(selectedCopy), member_id: selectedMember.id, days });
      toast.success('წიგნი გაიცა!');
      qc.invalidateQueries({ queryKey: ['checkouts'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
      qc.invalidateQueries({ queryKey: ['active-checkouts'] });
      onClose();
      setStep('book'); setSelectedBook(null); setSelectedMember(null);
      setSelectedCopy(''); setBookQ(''); setMemberQ('');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'შეცდომა');
    } finally { setLoading(false); }
  };

  const STEPS = [
    { key:'book', label:'1. წიგნი' },
    { key:'member', label:'2. წევრი' },
    { key:'confirm', label:'3. დადასტურება' },
  ];

  return (
    <Modal open={open} onClose={() => { onClose(); setStep('book'); }} title="წიგნის გაცემა" width={580}>
      <div style={{display:'flex',gap:4,marginBottom:20}}>
        {STEPS.map(s => (
          <div key={s.key} style={{flex:1,padding:'6px 8px',borderRadius:8,textAlign:'center',fontSize:11,fontWeight:500,
            background:step===s.key?'var(--bg-active)':selectedBook&&s.key==='book'||selectedMember&&s.key==='member'?'var(--success-bg)':'var(--bg-input)',
            color:step===s.key?'var(--accent)':selectedBook&&s.key==='book'||selectedMember&&s.key==='member'?'var(--success)':'var(--text-faint)',
            border:`1px solid ${step===s.key?'var(--border-active)':'var(--border)'}`,cursor:'pointer'}}
            onClick={() => {
              if(s.key==='member'&&!selectedBook) return;
              if(s.key==='confirm'&&(!selectedBook||!selectedMember)) return;
              setStep(s.key as any);
            }}>
            {s.label}
          </div>
        ))}
      </div>

      {step === 'book' && (
        <div>
          <div style={{position:'relative',marginBottom:12}}>
            <Search size={14} style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'var(--text-dimmed)'}} />
            <input value={bookQ} onChange={e => setBookQ(e.target.value)}
              placeholder="წიგნის სათაური, ავტორი ან ISBN..."
              autoFocus
              style={{width:'100%',background:'var(--bg-input)',border:'1px solid #1e2d45',borderRadius:8,padding:'9px 12px 9px 32px',fontSize:13,color:'var(--text-secondary)',outline:'none',boxSizing:'border-box'}} />
          </div>
          {bookQ.length > 1 && books.length === 0 && (
            <p style={{textAlign:'center',color:'var(--text-dimmed)',fontSize:13,padding:16}}>ვერ მოიძებნა</p>
          )}
          {books.map((b: any) => (
            <div key={b.id}
              onClick={() => {
                if(b.available_copies < 1) return toast.error('ხელმისაწვდომი ასლი არ არის');
                setSelectedBook(b);
                if(b.copies?.length === 1) setSelectedCopy(b.copies[0].id);
                setStep('member');
              }}
              style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',borderRadius:8,cursor:b.available_copies>0?'pointer':'not-allowed',marginBottom:4,
                background:selectedBook?.id===b.id?'var(--bg-active)':'var(--bg-input)',
                border:`1px solid ${selectedBook?.id===b.id?'var(--border-active)':'var(--border)'}`,
                opacity:b.available_copies>0?1:0.5}}>
              <div style={{width:32,height:40,borderRadius:4,background:'var(--accent-bg)',color:'var(--accent)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,fontWeight:700,flexShrink:0}}>
                {(b.title||'?')[0]}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:500,color:'var(--text-secondary)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{b.title}</div>
                <div style={{fontSize:11,color:'var(--text-faint)'}}>{b.author||'—'}</div>
              </div>
              <span style={{padding:'2px 8px',borderRadius:6,fontSize:11,fontWeight:500,flexShrink:0,
                background:b.available_copies>0?'var(--success-bg)':'var(--danger-bg)',
                color:b.available_copies>0?'var(--success)':'var(--danger)'}}>
                {b.available_copies} ხელმისაწვდომი
              </span>
            </div>
          ))}
          {selectedBook && (
            <button onClick={() => setStep('member')}
              style={{width:'100%',marginTop:12,padding:'9px',background:'#1d4ed8',border:'none',borderRadius:8,color:'white',fontSize:13,fontWeight:500,cursor:'pointer'}}>
              შემდეგი →
            </button>
          )}
        </div>
      )}

      {step === 'member' && (
        <div>
          {selectedBook && (
            <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',borderRadius:8,background:'var(--success-bg)',border:'1px solid #0a4a2a',marginBottom:12}}>
              <i className="ti ti-check" style={{color:'var(--success)',fontSize:16}} />
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,fontWeight:500,color:'var(--success)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{selectedBook.title}</div>
                <div style={{fontSize:11,color:'var(--text-faint)'}}>{selectedBook.author}</div>
              </div>
              <button onClick={() => { setSelectedBook(null); setStep('book'); }}
                style={{background:'none',border:'none',color:'var(--text-faint)',cursor:'pointer',fontSize:18}}>×</button>
            </div>
          )}
          <div style={{position:'relative',marginBottom:12}}>
            <Search size={14} style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'var(--text-dimmed)'}} />
            <input value={memberQ} onChange={e => setMemberQ(e.target.value)}
              placeholder="წევრის სახელი, ბარკოდი..."
              autoFocus
              style={{width:'100%',background:'var(--bg-input)',border:'1px solid #1e2d45',borderRadius:8,padding:'9px 12px 9px 32px',fontSize:13,color:'var(--text-secondary)',outline:'none',boxSizing:'border-box'}} />
          </div>
          {members.map((m: any) => (
            <div key={m.id}
              onClick={() => { setSelectedMember(m); setStep('confirm'); }}
              style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',borderRadius:8,cursor:'pointer',marginBottom:4,
                background:selectedMember?.id===m.id?'var(--bg-active)':'var(--bg-input)',
                border:`1px solid ${selectedMember?.id===m.id?'var(--border-active)':'var(--border)'}`}}>
              <div style={{width:32,height:32,borderRadius:8,background:'var(--accent-bg)',border:'1px solid #1e3a6e',display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:600,color:'var(--accent)',flexShrink:0}}>
                {(m.first_name?.[0]||'?').toUpperCase()}
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:500,color:'var(--text-secondary)'}}>{m.last_name} {m.first_name}</div>
                <div style={{fontSize:11,color:'var(--text-faint)'}}>{m.barcode||'—'} {m.email ? `· ${m.email}` : ''}</div>
              </div>
              {m.active_checkouts > 0 && (
                <span style={{padding:'2px 8px',borderRadius:6,fontSize:11,background:'var(--warning-bg)',color:'var(--warning)'}}>{m.active_checkouts} გაცემული</span>
              )}
            </div>
          ))}
        </div>
      )}

      {step === 'confirm' && selectedBook && selectedMember && (
        <form onSubmit={handleSubmit}>
          <div style={{background:'var(--bg-input)',border:'1px solid #1e2d45',borderRadius:10,padding:14,marginBottom:12}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:10}}>
              <div style={{padding:'8px 10px',background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:8}}>
                <div style={{fontSize:10,color:'var(--text-faint)',marginBottom:2}}>წიგნი</div>
                <div style={{fontSize:12,fontWeight:500,color:'var(--text-secondary)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{selectedBook.title}</div>
                <div style={{fontSize:11,color:'var(--text-faint)'}}>{selectedBook.author}</div>
              </div>
              <div style={{padding:'8px 10px',background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:8}}>
                <div style={{fontSize:10,color:'var(--text-faint)',marginBottom:2}}>წევრი</div>
                <div style={{fontSize:12,fontWeight:500,color:'var(--text-secondary)'}}>{selectedMember.last_name} {selectedMember.first_name}</div>
                <div style={{fontSize:11,color:'var(--text-faint)'}}>{selectedMember.barcode||'—'}</div>
              </div>
            </div>

            {selectedBook.copies?.length > 0 && (
              <FormField label="ასლი">
                <Select value={selectedCopy} onChange={e => setSelectedCopy(e.target.value)} required>
                  <option value="">— ასლი აირჩიე —</option>
                  {selectedBook.copies.filter((c: any) => c.status==='available').map((c: any) => (
                    <option key={c.id} value={c.id}>{c.barcode || `#${c.id}`} {c.call_number ? `· ${c.call_number}` : ''}</option>
                  ))}
                </Select>
              </FormField>
            )}

            <FormField label="გაცემის ვადა (დღე)">
              <div style={{display:'flex',alignItems:'center',gap:12}}>
                {[7,14,21,30].map(d => (
                  <button key={d} type="button" onClick={() => setDays(d)}
                    style={{padding:'6px 14px',borderRadius:8,border:`1px solid ${days===d?'var(--border-active)':'var(--border)'}`,
                      background:days===d?'var(--bg-active)':'var(--bg-input)',color:days===d?'var(--accent)':'var(--text-muted)',fontSize:12,cursor:'pointer',fontWeight:days===d?500:400}}>
                    {d}დ
                  </button>
                ))}
                <input type="number" value={days} onChange={e => setDays(parseInt(e.target.value)||14)} min={1} max={90}
                  style={{width:60,background:'var(--bg-input)',border:'1px solid #1e2d45',borderRadius:8,padding:'6px 8px',fontSize:12,color:'var(--text-secondary)',outline:'none',textAlign:'center'}} />
              </div>
            </FormField>

            <div style={{padding:10,background:'var(--accent-bg)',border:'1px solid #1e3a6e',borderRadius:8,fontSize:12,color:'var(--accent)'}}>
              <i className="ti ti-info-circle" style={{fontSize:14,marginRight:6}} />
              დაბრუნების ვადა: <strong>{new Date(Date.now()+days*86400000).toLocaleDateString('ka-GE')}</strong>
            </div>
          </div>

          <SubmitBtn loading={loading} label="გაცემა ✓" />
        </form>
      )}
    </Modal>
  );
}
